import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { AppDispatch, RootState } from '../store';
import {
  boardProcessExecuteRequest,
  boardProcessReset,
  boardProcessStatusRequest,
} from '../store/slices/boardProcess';
import {
  requestSignalBoardProcessExecute,
  requestSignalBoardProcessStatus,
} from '../services/signalApi';
import {
  BOARD_PROCESS_SUMMARY_REFRESH_MS,
  BOARD_PROCESS_WAIT_REFRESH_MS,
  BOARD_PROCESS_WAIT_TIME_MS,
  BOARD_TYPE_LABELS,
  boardTypeUsesProject,
  buildBoardProcessKey,
} from '../constants/boardProcessConsts';
import {
  formatBoardProcessDateTime,
  formatBoardProcessWaitTime,
  getRemainingBoardProcessWaitMs,
} from '../utils/boardProcess';

interface BoardProcessMenuProps {
  boardType: string;
  codProyecto?: string | null;
  onProcessed?: () => void;
}

export function BoardProcessMenu({ boardType, codProyecto, onProcessed }: BoardProcessMenuProps) {
  const dispatch = useDispatch<AppDispatch>();
  const authState = useSelector((state: RootState) => state.auth);
  const boardProcessState = useSelector((state: RootState) => state.boardProcess);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finishedOpen, setFinishedOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const requestingRef = useRef(false);
  const wasInProgressRef = useRef(false);

  const project = boardTypeUsesProject(boardType) ? (codProyecto || '').trim() : '';
  const boardKey = useMemo(() => buildBoardProcessKey(boardType, project), [boardType, project]);
  const boardLabel = BOARD_TYPE_LABELS[boardType] || boardType;
  const requiresProject = boardTypeUsesProject(boardType);
  const allowed = !requiresProject || !!project;

  const isCurrent = boardProcessState.boardKey === boardKey;
  const processedAt = isCurrent ? boardProcessState.processedAt : null;
  const status = isCurrent ? boardProcessState.status : null;
  const loading = isCurrent ? boardProcessState.loading : false;
  const executing = isCurrent && boardProcessState.executing;
  const ready = isCurrent && boardProcessState.responseCount > 0;
  const awaitingStatus =
    isCurrent &&
    !!boardProcessState.executedAt &&
    (!boardProcessState.statusReceivedAt ||
      boardProcessState.statusReceivedAt < boardProcessState.executedAt);
  const inProgress = isCurrent && (status === 0 || awaitingStatus);

  const requestStatus = useCallback(async () => {
    if (!authState.connectionId || requestingRef.current) {
      return;
    }
    requestingRef.current = true;
    dispatch(boardProcessStatusRequest({ boardKey }));
    try {
      await requestSignalBoardProcessStatus(boardType, project);
    } finally {
      requestingRef.current = false;
    }
  }, [authState.connectionId, boardKey, boardType, project, dispatch]);

  useEffect(() => {
    if (!authState.connectionId) {
      return;
    }
    dispatch(boardProcessReset());
    requestStatus();
  }, [authState.connectionId, boardKey, dispatch, requestStatus]);

  useEffect(() => {
    const interval = setInterval(
      () => setNow(Date.now()),
      inProgress ? BOARD_PROCESS_SUMMARY_REFRESH_MS : BOARD_PROCESS_WAIT_REFRESH_MS,
    );
    return () => clearInterval(interval);
  }, [inProgress]);

  useEffect(() => {
    if (!authState.connectionId || (!inProgress && !executing)) {
      return;
    }
    const interval = setInterval(() => requestStatus(), BOARD_PROCESS_SUMMARY_REFRESH_MS);
    return () => clearInterval(interval);
  }, [authState.connectionId, inProgress, executing, requestStatus]);

  useEffect(() => {
    if (inProgress) {
      wasInProgressRef.current = true;
      return;
    }
    if (wasInProgressRef.current && status === 1) {
      wasInProgressRef.current = false;
      setFinishedOpen(true);
    }
  }, [inProgress, status]);

  const remainingMs = getRemainingBoardProcessWaitMs(processedAt, now);
  const canProcess = allowed && ready && !inProgress && !executing && remainingMs === 0;

  const processedAtLabel = processedAt ? formatBoardProcessDateTime(processedAt) : '';
  const waitLabel = `Podrás procesar nuevamente en ${formatBoardProcessWaitTime(remainingMs)}`;

  const handleProcess = async () => {
    setConfirmOpen(false);
    if (!authState.connectionId || !canProcess) {
      return;
    }
    dispatch(boardProcessExecuteRequest({ boardKey }));
    try {
      await requestSignalBoardProcessExecute(boardType, project);
    } catch (error) {
      console.error('Error al procesar la información', error);
    }
    if (onProcessed) {
      onProcessed();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
      <Typography
        variant="caption"
        sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}
      >
        {!allowed
          ? 'Selecciona un proyecto'
          : inProgress || executing
            ? 'Procesando información...'
            : !ready
              ? 'Consultando estado...'
              : processedAtLabel
                ? `Procesado: ${processedAtLabel}`
                : 'Sin procesar'}
      </Typography>

      <Tooltip
        title={
          !allowed
            ? 'Selecciona un proyecto para procesar la información'
            : inProgress || executing
              ? 'Procesamiento en curso'
              : !ready
                ? 'Consultando el estado del procesamiento'
                : remainingMs > 0
                  ? waitLabel
                  : `Procesar información de ${boardLabel}`
        }
      >
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              loading || inProgress || executing ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <PlayArrowIcon fontSize="small" />
              )
            }
            disabled={!canProcess}
            onClick={() => setConfirmOpen(true)}
            sx={{ height: 30, textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Procesar
          </Button>
        </span>
      </Tooltip>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Procesar información</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            ¿Deseas procesar la información de {boardLabel}
            {project ? ` del proyecto ${project}` : ''} ahora?
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
            Solo se permite un procesamiento a la vez. Podrás volver a procesar después de{' '}
            {Math.round(BOARD_PROCESS_WAIT_TIME_MS / 60000)} minutos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleProcess}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Procesar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={finishedOpen}
        onClose={() => setFinishedOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Procesamiento finalizado</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            La información de {boardLabel}
            {project ? ` del proyecto ${project}` : ''} terminó de procesarse.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
            ¿Deseas recargar la página para ver la información actualizada?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinishedOpen(false)} sx={{ textTransform: 'none' }}>
            Ahora no
          </Button>
          <Button
            onClick={() => {
              setFinishedOpen(false);
              window.location.reload();
            }}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Recargar página
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
